import { IlocalStorageProvider } from "../../adapters/driven/providers/localStorageProvider";
import { IClinicRepository } from "../../adapters/driven/repositories/clinicRepository";

export const ClinicServiceImplFactory = ({
  clinicRepository,
  localStorageProvider,
  notificationService,
}: {
  clinicRepository: IClinicRepository;
  localStorageProvider: IlocalStorageProvider;
  notificationService: INotificationService;
}) => {
  const getMyClinics: IGetMyClinic = async () => {
    try {
      const response = await clinicRepository.getClinicID();
      let sortedClinicList: IClinic[] = [];
      if (response) {
        sortedClinicList = [...response];
        sortedClinicList = sortedClinicList
          .filter((clinic) => clinic.name.length)
          .sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
      }
      return {
        success: true,
        data: sortedClinicList,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const setSelectedClinic: ISetSelectedClinic = async ({
    id,
    clinicList,
    notificationSettings,
  }) => {
    await notificationService.setNotificationSetting(notificationSettings);
    return clinicList.find((item) => item.id === id) || null;
  };

  const getSelectedClinic: IGetSelectedClinic = ({ clinicList }) => {
    const selectedClinic = localStorageProvider.get("selectedClinic");

    if (selectedClinic) {
      return (
        clinicList.find((item) => item.id === Number(selectedClinic)) || null
      );
    }
    return null;
  };

  const setDefaultClinic: IGetSelectedClinic = ({ clinicList }) => {
    const clinic = clinicList[0];
    if (clinic) {
      localStorageProvider.set("selectedClinic", clinic.id.toString());
      return clinic;
    }
    return null;
  };

  const getClinicFloor: IGetClinicFloor = async ({ clinicId }) => {
    try {
      const response = await clinicRepository.getClinicFloor({
        clinic_id: clinicId,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const setClinicFloor: ISetClinicFloor = async (obj) => {
    try {
      const response = await clinicRepository.setClinicFloor({
        clinic_id: obj.clinicId,
        floor_ids: obj.floor_ids,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const logError: ILogError = async (obj) => {
    try {
      const response = await clinicRepository.logError(obj);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const fetchAssignedDevice: IFetchAssignedDevice = async (obj) => {
    try {
      const response = await clinicRepository.fetchAssignedDeviceAPI(obj);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const assingDeviceToAPatient: IAssingDeviceToAPatient = async (obj) => {
    try {
      const response = await clinicRepository.assingDeviceToAPatient(obj);
      // Success bodies carry `data` (the assigned device). Error bodies carry
      // `{ code?, message? }` without a `data` field. We use the presence of
      // `data` as the discriminator so the caller can render the backend's
      // friendly message / code instead of falling back to a generic string.
      if (response && typeof response === "object") {
        const r = response as Partial<{ data: IAssignedDevice }> & {
          message?: string;
          code?: string;
          patient_name?: string;
        };
        if (r.data && typeof r.data === "object") {
          return { success: true, data: r.data };
        }
        // Unassign (status 0) succeeds with no device to return — the backend
        // responds `{ data: null, message: "Device unassigned successfully" }`.
        // Absence of `data` is expected here, so only treat it as a failure
        // when the backend flags an error via `code` (error bodies carry it).
        if (obj.status === 0 && !r.code) {
          return { success: true };
        }
        return {
          success: false,
          error: {
            message:
              (typeof r.message === "string" && r.message) ||
              "Unexpected response from server.",
            code: typeof r.code === "string" ? r.code : undefined,
            patientName:
              typeof r.patient_name === "string" ? r.patient_name : undefined,
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message:
            error instanceof Error
              ? error.message
              : "Unexpected response from server.",
        },
      };
    }
  };

  const deleteAccount: IDeleteAccount = async (obj) => {
    try {
      const response = await clinicRepository.deleteAccount(obj);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const disconnectDevice: IDisconnectDevice = async (obj) => {
    // Defensive validation — the UI also blocks these but a missing
    // clinic_id / patient_id here would silently 404 against the
    // backend with a confusing error.
    if (!Number.isFinite(obj.clinic_id) || obj.clinic_id <= 0) {
      return {
        success: false,
        error: { message: "Missing clinic for this user." },
      };
    }
    if (!Number.isFinite(obj.patient_id) || obj.patient_id <= 0) {
      return {
        success: false,
        error: { message: "Missing patient identifier." },
      };
    }
    if (!obj.device_id || !obj.device_id.trim()) {
      return {
        success: false,
        error: { message: "Missing device identifier." },
      };
    }

    try {
      const response = await clinicRepository.disconnectDevice(obj);
      if (response && typeof response === "object") {
        const r = response as Partial<IDisconnectDeviceAPISuccess> & {
          detail?: string;
          message?: string;
          code?: string;
        };
        // Errors surface as `{ detail }` or `{ message, code }`. The
        // success envelope is unique in carrying a `device` object —
        // we use that as the discriminator so a stray `message` on an
        // error response can't be mistaken for success.
        if (r.device && typeof r.device === "object") {
          return {
            success: true,
            data: {
              message:
                typeof r.message === "string" && r.message
                  ? r.message
                  : "Device disconnected successfully.",
              device: r.device,
            },
          };
        }
        if (typeof r.detail === "string" && r.detail) {
          return {
            success: false,
            error: { message: r.detail, code: r.code },
          };
        }
        return {
          success: false,
          error: {
            message:
              (typeof r.message === "string" && r.message) ||
              "Unexpected response from server.",
            code: r.code,
          },
        };
      }
      return {
        success: false,
        error: { message: "Unexpected response from server." },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const setDeviceWifi: ISetDeviceWifi = async ({
    clinic_id,
    device_id,
    ssid,
    password,
    keepExisting = true,
    authType = 3,
    username = "",
    restartAfterSet = false,
  }) => {
    // Defensive validation — mirrors disconnectDevice so a missing scope
    // fails fast with a readable message instead of a confusing 404.
    if (!Number.isFinite(clinic_id) || clinic_id <= 0) {
      return {
        success: false,
        error: { message: "Missing clinic for this device." },
      };
    }
    if (!device_id || !device_id.trim()) {
      return {
        success: false,
        error: { message: "Missing device identifier." },
      };
    }
    if (!ssid || !ssid.trim()) {
      return {
        success: false,
        error: { message: "Wi-Fi network name (SSID) is required." },
      };
    }

    try {
      const response = await clinicRepository.setDeviceWifi({
        clinic_id,
        device_id: device_id.trim(),
        ssid: ssid.trim(),
        password,
        keep_existing: keepExisting,
        auth_type: authType,
        username,
        restart_after_set: restartAfterSet,
      });

      // The endpoint answers 200 with a confirmation on success. The
      // apiProvider swallows HTTP error codes and returns the error body, so
      // we can't discriminate on status here — instead we treat the presence
      // of any error marker (`detail` / `code` / `error` / `success: false`)
      // as a failure and everything else (including an empty 200) as success.
      //
      // `detail` comes in two shapes: a plain string (FastAPI default) or a
      // nested object `{ message }` — e.g. the 503 the device-command service
      // returns: `{ "detail": { "message": "Device command service is not
      // available" } }`. We normalise both so the UI never renders
      // "[object Object]".
      if (response && typeof response === "object") {
        const r = response as {
          detail?: string | { message?: string };
          message?: string;
          code?: string;
          error?: string;
          success?: boolean;
        };
        const detailMessage =
          typeof r.detail === "string"
            ? r.detail
            : r.detail && typeof r.detail === "object"
            ? r.detail.message
            : undefined;
        if (r.detail || r.code || r.error || r.success === false) {
          return {
            success: false,
            error: {
              message:
                detailMessage ||
                r.message ||
                r.error ||
                "Couldn't add Wi-Fi credentials to the device.",
              code: r.code,
            },
          };
        }
        return {
          success: true,
          data: {
            message:
              typeof r.message === "string" && r.message
                ? r.message
                : "Wi-Fi credentials added to the device.",
          },
        };
      }

      return {
        success: true,
        data: { message: "Wi-Fi credentials added to the device." },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getClinicFloor,
    getMyClinics,
    getSelectedClinic,
    setDefaultClinic,
    setSelectedClinic,
    setClinicFloor,
    logError,
    fetchAssignedDevice,
    assingDeviceToAPatient,
    deleteAccount,
    disconnectDevice,
    setDeviceWifi,
  };
};
